import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  ArrowLeft, Tv2, Loader2, Square, Play, Pause, SkipBack, SkipForward, Volume1, Volume2, X,
} from "lucide-react";
import { HlsPlayer } from "../components/HlsPlayer.js";
import { OutputPicker, getSavedTarget, type OutputTarget } from "../components/OutputPicker.js";
import { SubtitlePicker, type SelectedSubtitle } from "../components/SubtitlePicker.js";
import { getBearer } from "../lib/auth.js";
import { Api } from "../lib/api.js";

// On the TV kiosk itself there's nowhere to "send" — just play in place.
import { IS_TV } from "../lib/tvMode.js";

interface RemoteStatus { position: number; duration: number; paused: boolean; volume: number; }

/** A cast receiver as reported by GET /api/cast/devices. */
type CastDevice = { device_id: string; label: string; last_seen_ago_s: number; online: boolean };

/** localStorage key for the last TV a cast was sent to (pre-highlighted in the picker). */
const LAST_CAST_TARGET_KEY = "wallace_tv_cast_target";

/**
 * Direct-URL playback — used for Real-Debrid streams that aren't ingested
 * into media_items. HlsPlayer handles .m3u8 / direct MP4; mpv on the TV
 * handles everything (incl. UHD .mkv/HEVC/DTS-X the browser can't decode).
 *
 * Mirrors the main Watch page for these external streams:
 *  - Output target: "Send to TV" (default) hands the URL to mpv via
 *    /api/cast/mpv; "Play on This Browser" uses HlsPlayer.
 *  - Subtitles: OpenSubtitles picker when an IMDb id is passed
 *    (?imdb=…&mk=movie|series). Mounted as a <track> in-browser, or attached
 *    to mpv via sub_file_id / live /api/cast/mpv/subtitle while casting.
 *  - Full phone remote once the cast is live: scrubber, skip ±, play/pause,
 *    volume, subtitle switching, stop — driven by the global mpv command/status
 *    endpoints (no item_id needed). No watch_event tracking — external streams
 *    have no item_id to attribute progress to.
 */
export function WatchExternal(): JSX.Element {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const url = params.get("url") ?? "";
  const title = params.get("title") ?? "External stream";
  const poster = params.get("poster");
  const imdb = params.get("imdb");
  const mediaKind: "movie" | "series" = params.get("mk") === "series" ? "series" : "movie";
  const season = params.get("season") ? Number(params.get("season")) : undefined;
  const episode = params.get("episode") ? Number(params.get("episode")) : undefined;

  const [outputTarget, setOutputTarget] = useState<OutputTarget>(getSavedTarget);
  const [subtitle, setSubtitle] = useState<SelectedSubtitle | null>(null);
  // Browser playback only starts once the user chooses to play locally.
  const [playing, setPlaying] = useState(false);
  const [casting, setCasting] = useState(false);
  const [castSent, setCastSent] = useState(false);
  // Multi-TV: with >1 known receiver the cast click opens this chooser first
  // (same pattern as the library Watch page).
  const [castTargets, setCastTargets] = useState<CastDevice[] | null>(null);
  // The device the current cast went to — the remote targets the same TV.
  const castDeviceId = useRef<string | null>(null);
  const [remoteActive, setRemoteActive] = useState(false);
  const [remoteStatus, setRemoteStatus] = useState<RemoteStatus | null>(null);
  const [castError, setCastError] = useState<string | null>(null);
  // While dragging the slider we hold the target locally and ignore status
  // polls, so the thumb doesn't jump — and we seek exactly ONCE on release.
  const [scrubTo, setScrubTo] = useState<number | null>(null);
  // Exact "go to mm:ss" entry.
  const [jumpText, setJumpText] = useState("");

  const decodedTitle = decodeURIComponent(title);

  const mpvCommand = useCallback(async (action: string, value?: number) => {
    const token = getBearer() ?? "";
    await fetch("/api/cast/mpv/command", {
      method: "POST",
      headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
      body: JSON.stringify({ action, value, ...(castDeviceId.current ? { device_id: castDeviceId.current } : {}) }),
    }).catch(() => {/* non-fatal */});
  }, []);

  async function handlePlay(chosenDeviceId?: string): Promise<void> {
    // On the TV, or when explicitly set to browser, play here.
    if (IS_TV || outputTarget === "browser") {
      setPlaying(true);
      return;
    }
    setCasting(true);
    setCastError(null);
    try {
      // Multi-TV gate: with more than one known receiver, ask which TV first.
      // Picker failure falls through to the server default (the tunnel Pi).
      if (!chosenDeviceId) {
        try {
          const { devices } = await Api.cast.devices();
          if (devices.length > 1) {
            const lastUsed = localStorage.getItem(LAST_CAST_TARGET_KEY);
            setCastTargets(
              [...devices].sort((a, b) =>
                Number(b.online) - Number(a.online)
                || (a.device_id === lastUsed ? -1 : b.device_id === lastUsed ? 1 : 0)
                || a.last_seen_ago_s - b.last_seen_ago_s),
            );
            setCasting(false);
            return;
          }
        } catch { /* picker unavailable — cast to the default Pi */ }
      }
      const res = await Api.cast.mpv({
        url,
        sub_file_id: subtitle?.file_id ?? null,
        ...(chosenDeviceId ? { device_id: chosenDeviceId } : {}),
      });
      if (!res.ok) throw new Error(res.error ?? "Cast failed");
      if (chosenDeviceId) {
        localStorage.setItem(LAST_CAST_TARGET_KEY, chosenDeviceId);
        castDeviceId.current = chosenDeviceId;
      }
      setCastTargets(null);
      setCastSent(true);
    } catch (err) {
      setCastError((err as Error).message || "Couldn't reach the TV");
    } finally {
      setCasting(false);
    }
  }

  function handlePickCastTarget(deviceId: string): void {
    setCastTargets(null);
    void handlePlay(deviceId);
  }

  // Poll mpv state once cast — flips into full remote mode when it confirms
  // playing, and exits when mpv stops.
  useEffect(() => {
    if (!castSent) return;
    let cancelled = false;
    const token = getBearer() ?? "";
    const poll = async (): Promise<void> => {
      try {
        const r = await fetch(`/api/cast/mpv/status${castDeviceId.current ? `?device_id=${encodeURIComponent(castDeviceId.current)}` : ""}`, { headers: { Authorization: `Bearer ${token}` } });
        const d = await r.json() as { active: boolean; position?: number; duration?: number; paused?: boolean; volume?: number };
        if (cancelled) return;
        if (d.active) {
          setRemoteActive(true);
          setRemoteStatus({ position: d.position ?? 0, duration: d.duration ?? 0, paused: d.paused ?? false, volume: d.volume ?? 100 });
        } else if (remoteActive) {
          // mpv stopped — exit remote mode.
          setRemoteActive(false);
          setCastSent(false);
        }
      } catch { /* network blip */ }
    };
    void poll();
    const t = window.setInterval(() => void poll(), 3000);
    return () => { cancelled = true; window.clearInterval(t); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [castSent, remoteActive]);

  // Push the chosen subtitle to the TV's mpv whenever it changes mid-playback.
  const subtitleFileId = subtitle?.file_id ?? null;
  useEffect(() => {
    if (!remoteActive) return;
    const token = getBearer() ?? "";
    void fetch("/api/cast/mpv/subtitle", {
      method: "POST",
      headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
      body: JSON.stringify({ file_id: subtitleFileId, token, ...(castDeviceId.current ? { device_id: castDeviceId.current } : {}) }),
    }).catch(() => {/* non-fatal */});
  }, [remoteActive, subtitleFileId]);

  if (!url) {
    return (
      <div className="px-10 py-10">
        <div className="card p-8 text-vermilionHi">No URL provided.</div>
      </div>
    );
  }

  // ── Full phone remote — once the TV is confirmed playing ──────────────────
  if (remoteActive) {
    const pos = remoteStatus?.position ?? 0;
    const dur = remoteStatus?.duration ?? 0;
    const paused = remoteStatus?.paused ?? false;
    const volume = remoteStatus?.volume ?? 100;
    return (
      <div className="min-h-screen bg-lacquer-deep flex flex-col" style={{ fontFamily: "'Cinzel', serif" }}>
        {poster && (
          <div className="absolute inset-0 overflow-hidden">
            <img src={poster} alt="" className="w-full h-full object-cover opacity-20" referrerPolicy="no-referrer" />
            <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(10,4,4,1) 30%, rgba(10,4,4,0.7) 100%)" }} />
          </div>
        )}
        <div className="relative z-10 flex flex-col flex-1 items-center justify-center gap-8 px-6 py-10 text-center">
          <div>
            <div className="section-label text-vermilion tracking-[0.4em] text-xs mb-2">PLAYING ON TV</div>
            <h1 className="font-display text-ivory text-2xl leading-tight break-words" style={{ letterSpacing: "0.06em" }}>{decodedTitle}</h1>
          </div>

          {/* Precise seek — slider commits ONCE on release; the time field
              jumps to an exact timestamp with a single command. */}
          <div className="w-full max-w-sm flex flex-col gap-3">
            <input
              type="range"
              min={0}
              max={dur > 0 ? Math.floor(dur) : 1}
              step={1}
              value={scrubTo ?? Math.floor(pos)}
              disabled={dur <= 0}
              onChange={(e) => setScrubTo(Number(e.currentTarget.value))}
              onPointerUp={(e) => {
                const v = Number((e.currentTarget as HTMLInputElement).value);
                setScrubTo(null);
                if (dur > 0) void mpvCommand("seek_to", v);
              }}
              onKeyUp={(e) => {
                // Keyboard nudge (arrows) also commits once on key release.
                if (scrubTo === null) return;
                setScrubTo(null);
                if (dur > 0) void mpvCommand("seek_to", scrubTo);
              }}
              className="w-full accent-vermilion h-2 cursor-pointer"
              aria-label="Seek"
            />
            <div className="flex justify-between text-parchment/50 text-xs font-mono">
              <span className={scrubTo !== null ? "text-goldHi" : ""}>{fmtTime(scrubTo ?? Math.floor(pos))}</span>
              {dur > 0 && <span>{fmtTime(Math.floor(dur))}</span>}
            </div>

            <form
              className="flex items-center gap-2"
              onSubmit={(e) => {
                e.preventDefault();
                const secs = parseTimecode(jumpText);
                if (secs === null) return;
                const clamped = dur > 0 ? Math.min(secs, Math.floor(dur)) : secs;
                void mpvCommand("seek_to", clamped);
                setJumpText("");
              }}
            >
              <input
                value={jumpText}
                onChange={(e) => setJumpText(e.target.value)}
                inputMode="numeric"
                placeholder="Go to  e.g. 10:23"
                className="flex-1 bg-ink/60 border border-hairline rounded-md px-3 py-2 text-ivory text-sm font-mono text-center focus:border-gold outline-none tabular-nums"
              />
              <button
                type="submit"
                disabled={parseTimecode(jumpText) === null}
                className="pill-ghost text-sm px-4 py-2 disabled:opacity-40"
              >
                Go
              </button>
            </form>
          </div>

          {/* Transport */}
          <div className="flex items-center gap-8">
            <button onPointerDown={() => void mpvCommand("seek_back", 10)}
              className="flex flex-col items-center gap-2 p-4 rounded-2xl active:bg-white/10 text-parchment/80 transition-colors">
              <SkipBack size={36} /><span className="text-xs tracking-widest">10s</span>
            </button>
            <button onPointerDown={() => void mpvCommand("pause")}
              className="w-24 h-24 rounded-full bg-vermilion border-2 border-goldHi flex items-center justify-center text-ivory active:scale-95 transition-transform shadow-2xl">
              {paused ? <Play size={40} /> : <Pause size={40} />}
            </button>
            <button onPointerDown={() => void mpvCommand("seek_fwd", 30)}
              className="flex flex-col items-center gap-2 p-4 rounded-2xl active:bg-white/10 text-parchment/80 transition-colors">
              <SkipForward size={36} /><span className="text-xs tracking-widest">30s</span>
            </button>
          </div>

          {/* Volume */}
          <div className="flex items-center gap-4">
            <button onPointerDown={() => void mpvCommand("volume_down", 10)}
              className="flex flex-col items-center gap-1 p-3 rounded-2xl active:bg-white/10 text-parchment/70 transition-colors">
              <Volume1 size={28} /><span className="text-xs tracking-widest">VOL−</span>
            </button>
            <div className="text-parchment/40 text-sm font-mono w-12 text-center">{Math.round(volume)}%</div>
            <button onPointerDown={() => void mpvCommand("volume_up", 10)}
              className="flex flex-col items-center gap-1 p-3 rounded-2xl active:bg-white/10 text-parchment/70 transition-colors">
              <Volume2 size={28} /><span className="text-xs tracking-widest">VOL+</span>
            </button>
          </div>

          {/* Subtitles — adjustable live */}
          {imdb && (
            <div className="flex flex-col items-center gap-3 w-full max-w-xs">
              <SubtitlePicker
                imdbId={imdb}
                kind={mediaKind}
                season={season}
                episode={episode}
                selected={subtitle}
                onSelect={setSubtitle}
              />
            </div>
          )}

          <button
            onPointerDown={async () => {
              await mpvCommand("stop");
              setRemoteActive(false);
              setCastSent(false);
            }}
            className="flex items-center gap-2 px-8 py-4 rounded-full border border-hairline text-parchment/60 text-sm active:bg-white/10 transition-colors"
          >
            <Square size={16} /> Stop
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-[1100px] mx-auto px-10 py-8">
      <div className="flex items-center justify-between gap-3 mb-4">
        <button onClick={() => nav(-1)} className="pill-ghost">
          <ArrowLeft size={14} /> Back
        </button>
        {!playing && !castSent && (
          <div className="flex items-center gap-2">
            {imdb && (
              <SubtitlePicker
                imdbId={imdb}
                kind={mediaKind}
                season={season}
                episode={episode}
                selected={subtitle}
                onSelect={setSubtitle}
              />
            )}
            {!IS_TV && <OutputPicker value={outputTarget} onChange={setOutputTarget} />}
          </div>
        )}
      </div>

      {playing ? (
        <div className="rounded-lg overflow-hidden border border-hairline aspect-video bg-ink">
          <HlsPlayer src={url} poster={poster ?? null} subtitle={subtitle} />
        </div>
      ) : castSent ? (
        // Sent, waiting for mpv to confirm — brief spinner before the remote.
        <div className="rounded-lg overflow-hidden border border-hairline aspect-video bg-ink relative flex flex-col items-center justify-center gap-4">
          {poster && (
            <img src={poster} alt="" referrerPolicy="no-referrer"
              className="absolute inset-0 w-full h-full object-cover opacity-20" />
          )}
          <div className="relative z-10 flex flex-col items-center gap-3">
            <Loader2 size={32} className="text-gold animate-spin" />
            <div className="section-label text-gold tracking-[0.3em] text-xs">SENDING TO TV…</div>
            <p className="text-parchment/40 text-xs">Waiting for the TV to start…</p>
            <button className="pill-ghost text-xs mt-1" onClick={() => setCastSent(false)}>← Cancel</button>
          </div>
        </div>
      ) : (
        <button
          onClick={() => void handlePlay()}
          disabled={casting}
          className="w-full rounded-lg overflow-hidden border border-hairline aspect-video bg-ink relative flex flex-col items-center justify-center gap-4 hover:border-gold transition-colors group disabled:opacity-70"
        >
          {poster && (
            <img src={poster} alt="" referrerPolicy="no-referrer"
              className="absolute inset-0 w-full h-full object-cover opacity-25 group-hover:opacity-30 transition-opacity" />
          )}
          <div className="relative z-10 flex flex-col items-center gap-3">
            {casting ? (
              <>
                <Loader2 size={36} className="text-gold animate-spin" />
                <span className="section-label text-gold tracking-[0.3em] text-xs">SENDING TO TV…</span>
              </>
            ) : outputTarget === "wallace-tv" && !IS_TV ? (
              <>
                <Tv2 size={40} className="text-jade" />
                <span className="font-display tracking-smallcaps text-ivory">Send to TV</span>
              </>
            ) : (
              <>
                <span className="text-gold text-5xl leading-none">▶</span>
                <span className="font-display tracking-smallcaps text-ivory">Play here</span>
              </>
            )}
          </div>
        </button>
      )}

      {/* Multi-TV chooser — appears after a Send-to-TV click when the server
          knows more than one receiver. */}
      {castTargets && (
        <div className="mt-3 rounded-xl border border-hairline bg-elev/90 backdrop-blur p-2">
          <div className="flex items-center justify-between px-2 py-1">
            <span className="section-label text-gold">Send to which TV?</span>
            <button
              onClick={() => setCastTargets(null)}
              className="text-parchment/60 hover:text-ivory transition-colors"
              title="Cancel"
            >
              <X size={14} />
            </button>
          </div>
          {castTargets.map((d) => (
            <button
              key={d.device_id}
              onClick={() => handlePickCastTarget(d.device_id)}
              className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left hover:bg-lacquer transition-colors"
            >
              <span
                className="w-2 h-2 rounded-full shrink-0"
                style={{ background: d.online ? "#7fb069" : "rgba(199,162,74,0.35)" }}
              />
              <span className="font-display text-ivory text-sm">{d.label}</span>
              {d.device_id === localStorage.getItem(LAST_CAST_TARGET_KEY) && (
                <span className="text-gold text-[10px] uppercase tracking-smallcaps">last used</span>
              )}
              {!d.online && (
                <span className="ml-auto text-parchment/50 text-xs tabular-nums">
                  {d.last_seen_ago_s < 90
                    ? "just now"
                    : d.last_seen_ago_s < 3600
                      ? `${Math.round(d.last_seen_ago_s / 60)}m ago`
                      : `${Math.round(d.last_seen_ago_s / 3600)}h ago`}
                </span>
              )}
            </button>
          ))}
        </div>
      )}

      {castError && <p className="text-vermilionHi text-xs mt-3">{castError}</p>}

      <div className="mt-6">
        <div className="section-label">Real-Debrid stream</div>
        <h1 className="hero-title mt-2 break-words">{decodedTitle}</h1>
        <p className="text-parchment text-xs mt-2 break-all opacity-50">{url}</p>
      </div>
    </div>
  );
}

/**
 * Parse a flexible timecode into seconds, or null if unparseable/empty.
 * Accepts "623" (raw seconds), "10:23" (mm:ss), "1:10:23" (h:mm:ss).
 */
function parseTimecode(input: string): number | null {
  const t = input.trim();
  if (!t) return null;
  const parts = t.split(":").map((p) => p.trim());
  if (parts.some((p) => !/^\d+$/.test(p)) || parts.length > 3) return null;
  let secs = 0;
  for (const p of parts) secs = secs * 60 + Number(p);
  return Number.isFinite(secs) ? secs : null;
}

function fmtTime(s: number): string {
  if (!Number.isFinite(s) || s < 0) s = 0;
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = Math.floor(s % 60);
  return h > 0
    ? `${h}:${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`
    : `${m}:${String(sec).padStart(2, "0")}`;
}
